import React, { useState, useEffect } from "react";
import styled from "styled-components";
import downloadArrowIcon from "../../images/banners/download-arrow.svg";
import exclamationIcon from "../../images/banners/exclamation.svg";

const BannerContainer = styled.div`
  position: relative;
  width: 520px;
  min-height: 90px;
  border-radius: 105px 0 0 105px;
  padding: 16px;
  transform: translateX(calc(100% - 120px));
  transition: transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  cursor: pointer;

  @media (max-width: 768px) {
    width: 350px;
    min-height: 79px;
    padding: 16px;
    transform: translateX(calc(100% - 100px));
  }

  &.expanded {
    transform: translateX(0);
  }
`;

const BannerWrapper = styled.div`
  position: absolute;
  top: 20px;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
  z-index: 1000;

  @media (max-width: 768px) {
    top: 15px;
    gap: 15px;
  }
`;

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconContainer = styled.div`
  flex-shrink: 0;
  width: 70px;
  height: 70px;

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
  }
`;

const Icon = styled.img`
  width: 100%;
  height: 100%;
`;

const TextContainer = styled.div`
  flex: 1;
  color: #ec76df;
`;

const Title = styled.h3`
  font-family: "Kinder";
  font-size: 20px;
  font-weight: 400;
  margin: 0 0 8px 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const Subtitle = styled.p`
  font-family: "Helvetica Neue", Arial, sans-serif;
  font-size: 13px;
  margin: 0;
  line-height: 1.3;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const banners = [
  {
    id: "talent-contact",
    icon: downloadArrowIcon,
    title: "¿Te interesa trabajar con nosotros?",
    subtitle:
      "Si eres freelance o te interesa sumarte a nuestro equipo, déjanos aquí tus datos.",
    url: "https://talent.itsgreener.com/",
    backgroundColor: "#1941f5",
    textColor: "#ec76df",
    active: true,
  },
  {
    id: "fruit-attraction",
    icon: exclamationIcon,
    title: "Listen to Brand The Future",
    subtitle:
      "Click here and Join the conversation on the future of agriculture on Greener’s new podcast.",
    url: "https://www.youtube.com/playlist?list=PLn63IEA4EbA8wjo7tLHwUE4SDwVAjXUgg",
    backgroundColor: "#f4e600", // Amarillo
    textColor: "#1941f5", // Azul
    active: true,
  },
];

const AnimatedBanners = () => {
  const [expandedStates, setExpandedStates] = useState({});
  const [timers, setTimers] = useState({});

  useEffect(() => {
    // Expandir todos los banners activos inmediatamente
    const activeBanners = banners.filter((banner) => banner.active);
    const initialStates = {};
    const initialTimers = {};

    activeBanners.forEach((banner, index) => {
      // Delay escalonado para cada banner
      const expandDelay = 100 + index * 200;

      initialStates[banner.id] = false;

      const expandTimer = setTimeout(() => {
        setExpandedStates((prev) => ({ ...prev, [banner.id]: true }));
      }, expandDelay);

      const collapseTimer = setTimeout(() => {
        setExpandedStates((prev) => ({ ...prev, [banner.id]: false }));
      }, expandDelay + 15100);

      initialTimers[banner.id] = { expandTimer, collapseTimer };
    });

    setExpandedStates(initialStates);
    setTimers(initialTimers);

    return () => {
      Object.values(initialTimers).forEach(({ expandTimer, collapseTimer }) => {
        clearTimeout(expandTimer);
        clearTimeout(collapseTimer);
      });
    };
  }, []);

  const handleIconClick = (bannerId) => {
    if (!expandedStates[bannerId]) {
      setExpandedStates((prev) => ({ ...prev, [bannerId]: true }));

      // Limpiar timer anterior si existe
      if (timers[bannerId]) {
        clearTimeout(timers[bannerId].collapseTimer);
      }

      // Reiniciar timer de 15 segundos
      const collapseTimer = setTimeout(() => {
        setExpandedStates((prev) => ({ ...prev, [bannerId]: false }));
      }, 15000);

      setTimers((prev) => ({
        ...prev,
        [bannerId]: { ...prev[bannerId], collapseTimer },
      }));
    }
  };

  const handleTextClick = (e, banner) => {
    e.stopPropagation();
    if (banner?.url) {
      if (banner.url.startsWith("http")) {
        window.open(banner.url, "_blank");
      } else {
        window.location.href = banner.url;
      }
    }
  };

  const activeBanners = banners.filter((banner) => banner.active);

  if (activeBanners.length === 0) return null;

  return (
    <BannerWrapper>
      {activeBanners.map((banner, index) => (
        <BannerContainer
          key={banner.id}
          className={expandedStates[banner.id] ? "expanded" : ""}
          onClick={() => handleIconClick(banner.id)}
          style={{
            backgroundColor: banner.backgroundColor,
          }}
        >
          <BannerContent>
            <IconContainer>
              <Icon src={banner.icon} alt="Banner icon" />
            </IconContainer>
            {expandedStates[banner.id] && (
              <TextContainer
                onClick={(e) => handleTextClick(e, banner)}
                style={{ color: banner.textColor }}
              >
                <Title>{banner.title}</Title>
                <Subtitle>{banner.subtitle}</Subtitle>
              </TextContainer>
            )}
          </BannerContent>
        </BannerContainer>
      ))}
    </BannerWrapper>
  );
};

export default AnimatedBanners;
